import { computed, inject, Ref, isRef, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDynamicDefaults } from '@shared/composables/useDynamicDefaults';

interface Parameter {
  name: string;
  description?: string;
}

/**
 * Resolve dynamic placeholders (e.g. {{fieldId}}, {{paramName}}, {{CURRENT_DATE}}) inside a text string.
 * Usage:
 *   const resolved = useDynamicContent(toRef(props, 'content'), props.parameters)
 */
export function useDynamicContent(
  contentRef: Ref<string | undefined>,
  parameters: Parameter[] = []
) {
  const injectedData = inject<Record<string, any> | Ref<Record<string, any>>>('formData', {});
  const formData = isRef(injectedData) ? injectedData : ref(injectedData);
  const route = useRoute();

  // Inject form elements for label lookup
  const injectedElements = inject<any[] | Ref<any[]>>('formElements', []);
  const formElements = isRef(injectedElements) ? injectedElements : ref(injectedElements);

  const { SPECIAL_VALUES } = useDynamicDefaults();

  const getSpecialValue = (key: string): string | undefined => {
    const specialValueMap: Record<string, () => string> = {
      USER_IP: () => 'USER_IP', // TODO: replace with actual IP fetch if needed
      CURRENT_DATE: () => new Date().toISOString().split('T')[0],
      CURRENT_TIME: () => new Date().toTimeString().split(' ')[0],
      CURRENT_DATETIME: () => new Date().toISOString(),
    };
    return specialValueMap[key]?.();
  };

  const getParameterValue = (paramName: string): string | undefined => {
    if (route.query && paramName in route.query) {
      return String(route.query[paramName]);
    }
    const param = parameters.find((p) => p.name === paramName);
    if (param) {
      return '';
    }
    return undefined;
  };

  const getFieldValue = (fieldId: string): string | undefined => {
    const value = formData.value[fieldId];
    if (value === undefined || value === null) return undefined;
    // Zoek het element op in het formulier
    const element = formElements.value.find((el: any) => el.id === fieldId);
    if (!element) return String(value);
    // Check of het een keuzevraag is
    if (['single-choice', 'multiple-choice', 'picture-choice'].includes(element.type)) {
      const choices = element.props.choices || [];
      if (Array.isArray(value)) {
        // Multi-choice: map alle ids naar labels
        return value
          .map((val) => choices.find((c: any) => c.id === val)?.content || val)
          .join(', ');
      } else {
        // Single-choice: zoek label
        return choices.find((c: any) => c.id === value)?.content || value;
      }
    }
    // Anders: toon de ruwe waarde
    return String(value);
  };

  const resolvedContent = computed(() => {
    const content = contentRef.value || '';
    const placeholders = Array.from(content.matchAll(/\{\{([^}]+)\}\}/g));

    const resolvePlaceholder = (key: string): string | undefined => {
      if (SPECIAL_VALUES.includes(key)) {
        const sv = getSpecialValue(key);
        if (sv !== undefined) return sv;
      }

      const pv = getParameterValue(key);
      if (pv !== undefined) return pv;

      const fv = getFieldValue(key);
      if (fv !== undefined) return fv;

      return undefined;
    };

    return content.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
      const trimmedKey = key.trim();
      return resolvePlaceholder(trimmedKey) || '';
    }).trim();
  });

  return resolvedContent;
}
